import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { useAccount } from 'wagmi';

import { DepositToPublishModal } from 'src/components/DepositToPublishModal';
import { defaultProjectInput } from 'src/global/constants/project.constant';
import { ProjectInputSchema } from 'src/global/schemas/project.schema';
import { ProjectInputs } from 'src/global/types/project.type';
import { useDomainVerification } from 'src/hooks/useDomainVerification';
import { useSaveDraft } from 'src/hooks/useSaveDraft';
import { useStagingPublish } from 'src/hooks/useStagingPublish';
import { useTheme } from 'src/provider/ThemeCustomizationProvider';
import { useCreateProjectMutation, useGetAdminWalletQuery } from 'src/store/services/project.service';

import { FundingDistribution } from './FundingDistribution/FundingDistribution';
import GeneralProjectInfo from './GeneralInfo/GeneralInfo';
import { NodeSaleActionButtons } from './NodeSaleActionButtons';
import NodeSaleSettings from './NodeSaleSettings/NodeSaleSettings';
import { PricingModel } from './PricingModel/PricingModel';
import ReferralProgram from './ReferralProgram/ReferralProgram';
import { RoyaltyFee } from './RoyaltyFee/RoyaltyFee';
import { ThemeCustomization } from './ThemeCustomization/ThemeCustomization';
import { TokenConfiguration } from './TokenConfiguration/TokenConfiguration';
import { VerifyDNSModal, VerifyDNSModalHandle } from './VerifyDNSModal';

export const CreateNodeSaleForm = () => {
  const { theme, executeThemeValidation } = useTheme();
  const [generatedCuid, setGeneratedCuid] = useState<string>('');
  const { address } = useAccount();
  const [showDepositModal, setShowDepositModal] = useState(false);
  const methods = useForm<ProjectInputs>({
    resolver: zodResolver(ProjectInputSchema),
    defaultValues: defaultProjectInput,
    mode: 'all',
    reValidateMode: 'onChange',
  });
  const [createProject, { isLoading: isCreatingProject }] = useCreateProjectMutation();
  const { data: adminWallet, isLoading: isLoadingAdminWallet } = useGetAdminWalletQuery(
    { address: address as string },
    { skip: !address },
  );
  const {
    isDomainDnsVerified,
    isDomainVerified,
    isLoading: isVerifyingDomain,
    verifyDomainData,
    verifyAndCreateDomain,
    verifyDomain,
  } = useDomainVerification(methods, address as string | undefined);

  const { saveDraftAsync, isSaving } = useSaveDraft(methods.trigger);
  const { publishStagingAsync, isPublishing } = useStagingPublish();
  const nagivate = useNavigate();

  const initAttemptedRef = useRef(false);
  const verifyDnsModalRef = useRef<VerifyDNSModalHandle>(null);
  const draftRef = useRef<any>(null);

  const goHome = useCallback(() => nagivate('/core-sale'), [nagivate]);

  const isProjectPhaseValid = useCallback(() => {
    const phases = methods.getValues('phases');
    const tierAutoAdjustment = methods.getValues('tier.autoAdjustment');
    const nodeSupplyCap = methods.getValues('tier.nodeSupplyCap');
    const totalLayerSupply = (methods.getValues('tier.layer') || []).reduce(
      (sum, tier: { total: number }) => sum + tier.total,
      0,
    );
    const limitNodeSupply = tierAutoAdjustment ? nodeSupplyCap : totalLayerSupply;

    const totalNodeSoldPhases = phases.reduce((sum, phase) => sum + phase.totalNode, 0);
    if (totalNodeSoldPhases !== limitNodeSupply) {
      methods.setError('phases', {
        type: 'manual',
        message:
          'Total phase nft sales must match either the total NFT Supply Tier or the Total NFT Supply Cap in the Pricing Model. If there are remaining nodes, set up an additional phase to allocate them accordingly.',
      });
      return false;
    }
    return true;
  }, [methods]);

  useEffect(() => {
    if (!address || generatedCuid || initAttemptedRef.current) return;

    initAttemptedRef.current = true;

    const initProject = async () => {
      try {
        const response = await createProject({ creator: address as string });
        setGeneratedCuid(response.data.id);
      } catch (error) {}
    };

    initProject();
  }, [address, generatedCuid, createProject]);

  const handleSaveDraft = useCallback(async () => {
    try {
      const isValid = await methods.trigger('name');
      if (!isValid || !address || !theme) return;

      const formValues = methods.getValues();
      await saveDraftAsync({ id: generatedCuid }, { ...formValues }, theme);
      toast.success('Save draft successfully');
      goHome();
    } catch (error: any) {
      toast.error(error?.message ?? 'Save draft failed');
    }
  }, [methods, address, generatedCuid, theme, saveDraftAsync, goHome]);

  const publishProject = useCallback(async () => {
    try {
      if (!draftRef.current || !generatedCuid) throw new Error('Draft is missing');
      await publishStagingAsync({ id: generatedCuid }, methods.getValues(), draftRef.current);
      toast.success('Publish to staging successfully');

      // Show deposit modal after successful publish
      setShowDepositModal(true);
    } catch (error: any) {
      toast.error(error?.data?.message || error?.message || 'Publish project failed');
    }
  }, [generatedCuid, methods, publishStagingAsync]);

  const onSubmit = useCallback(async () => {
    if (!isProjectPhaseValid()) return;
    try {
      const invalid = executeThemeValidation?.();
      if (invalid || !address || !theme) return;

      const formValues = methods.getValues();

      if (!draftRef.current) {
        draftRef.current = await saveDraftAsync({ id: generatedCuid }, formValues, theme, true);
        if (!draftRef.current) throw new Error('Save draft failed');
      }

      const verified = await verifyAndCreateDomain(() => verifyDnsModalRef?.current?.show());
      if (!verified) return;

      await publishProject();
    } catch (err: any) {
      console.log('🚀 ~ err:', err);
      toast.error(err?.data?.message || err?.message || 'Publish project failed');
    }
  }, [
    isProjectPhaseValid,
    generatedCuid,
    theme,
    methods,
    executeThemeValidation,
    address,
    saveDraftAsync,
    verifyAndCreateDomain,
    publishProject,
  ]);

  const handleVerifyDomain = useCallback(async () => {
    try {
      const verified = await verifyDomain();
      if (!verified) {
        toast.info('DNS may take time to propagate. Please try again later.');
        return;
      }

      verifyDnsModalRef?.current?.hide();
      await publishProject();
    } catch {
      toast.error('Publish project failed');
    }
  }, [verifyDomain, publishProject]);

  const handleDeployProject = useCallback(async () => {
    if (!isProjectPhaseValid()) return;
    executeThemeValidation?.();
    methods.handleSubmit(onSubmit)();
  }, [isProjectPhaseValid, executeThemeValidation, methods, onSubmit]);

  if (!generatedCuid || isCreatingProject) return null;

  return (
    <FormProvider {...methods}>
      <form className='flex flex-col gap-y-4' onSubmit={methods.handleSubmit(onSubmit)}>
        <GeneralProjectInfo />
        <TokenConfiguration programId={generatedCuid} />
        <NodeSaleSettings />
        <PricingModel />
        <ReferralProgram />
        <FundingDistribution adminWallet={adminWallet} />
        <RoyaltyFee />
        <ThemeCustomization />
        {/* <NodeSystemIntegration /> */}

        <NodeSaleActionButtons
          isSaving={isSaving}
          isPublishing={isPublishing}
          deployProject={handleDeployProject}
          handleSaveDraft={handleSaveDraft}
        />

        <VerifyDNSModal
          ref={verifyDnsModalRef}
          onSubmit={handleVerifyDomain}
          loading={isVerifyingDomain}
          verifyDomainData={verifyDomainData}
          isVerified={isDomainVerified}
          isDnsVerified={isDomainDnsVerified}
        />

        <DepositToPublishModal
          open={showDepositModal}
          programId={generatedCuid}
          onClose={() => {
            setShowDepositModal(false);
            goHome();
          }}
          onSuccess={_ => {
            setShowDepositModal(false);
            toast.success('The transaction hash has been successfully verified');
            goHome();
          }}
        />
      </form>
    </FormProvider>
  );
};
